import {useTheme} from '@mui/material/styles';

import {makeStyles} from 'tss-react/mui';
import {AxiosError} from 'axios';
import {StatusCodes} from 'http-status-codes';
import React, {Dispatch, SetStateAction, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MutationStatus} from 'react-query';
import {
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  CustomSelect,
  HtmlTrans,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  SectionHeader,
  stringToSemanticDisplay,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
} from 'web-library';
import {AUTHORITY_NAMES} from 'web-library/models/AuthorityFunctions';
import {IconName} from 'web-library/stories/atom/Icon/Icon';
import {useDeleteAdmissionLevelVacancies} from '../../../../api-refactored/exam-configuration/useDeleteAdmissionLevelVacancies';
import {
  AdmissionLevelVacanciesModel,
  useGetAdmissionLevelVacancies,
} from '../../../../api-refactored/exam-configuration/useGetAdmissionLevelVacancies';
import {useGetAdmissionYears} from '../../../../api-refactored/exam-configuration/useGetAdmissionYears';
import {useGetLastUpdatedInfo} from '../../../../api-refactored/exam-configuration/useGetLastUpdatedInfo';
import {useGetUnusedExamLevels} from '../../../../api-refactored/exam-configuration/useGetUnusedExamLevels';
import {useGetUserAuthorities} from '../../../../api-refactored/user/useGetUserAuthorities';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import DraftDateTimeStamp from '../../../../components/draft-date-time-stamp/DraftDateTimeStamp';
import {existInArray} from '../../../../utils/ArrayUtils';
import AddAdmissionLevelModal from './AddAdmissionLevelModal';

const useStyles = makeStyles()((theme) =>
    ({
      root: {},
      headerContainer: {
        marginTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        display: 'flex',
        justifyContent: 'space-between',
        [theme.breakpoints.down('md')]: {
          display: 'block',
          paddingBottom: theme.spacing(1),
        },
      },
      addAdmissionLevelButton: {
        minWidth: '200px',
        marginTop: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
          marginTop: theme.spacing(1),
        },
      },
    }));

let date = new Date();
const offset = date.getTimezoneOffset();
date = new Date(date.getTime() - offset * 60 * 1000);
const currentYear = date.getFullYear();

const defaultDateOfBirthFrom = `Jan 02 ${currentYear}`;
const defaultDateOfBirthTo = `Jan 01 ${currentYear}`;

const useGetTableColumns = (
    data: AdmissionLevelVacanciesModel[],
    hasDeleteAccess: boolean,
    setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
    setDeletedAdmissionLevel: React.Dispatch<React.SetStateAction<string>>,
    setDeletedAdmissionLevelUuid: React.Dispatch<React.SetStateAction<string>>
): MUIDataTableColumnDef[] => {
  const {t} = useTranslation();

  return [
    {
      name: 'admissionLevel',
      label: t('exam->>ExamConfiguration.AdmissionLevel'),
      options: {
        sort: false,
      },
    },
    {
      name: 'dateOfBirthFrom',
      label: t('exam->>ExamConfiguration.DateofBirthFrom'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
            <Typography variant="body2">
              {stringToSemanticDisplay(data[dataIndex].dateOfBirthFrom)}
            </Typography>
        ),
      },
    },
    {
      name: 'dateOfBirthTo',
      label: t('exam->>ExamConfiguration.DateofBirthTo'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
            <Typography variant="body2">
              {stringToSemanticDisplay(data[dataIndex].dateOfBirthTo)}
            </Typography>
        ),
      },
    },
    {
      name: 'totalExamVacancy',
      label: t('exam->>ExamConfiguration.TotalExamVacancy'),
      options: {
        sort: false,
      },
    },
    {
      name: 'vacanciesLeft',
      label: t('exam->>ExamConfiguration.VacanciesLeft'),
      options: {
        sort: false,
      },
    },
    {
      name: 'actions',
      label: t('exam->>ExamConfiguration.Actions'),
      options: {
        display: hasDeleteAccess,
        sort: false,
        customBodyRenderLite: (dataIndex: number) =>
            DeleteButton({
              deleteHandler: () => {
                setDeletedAdmissionLevel(data[dataIndex].admissionLevel);
                setDeletedAdmissionLevelUuid(data[dataIndex].uuid);
                setOpenDeleteModal(true);
              },
              label: t('exam->>IconLabel.Remove'),
              buttonId: dataIndex,
            }),
      },
    },
  ];
};

interface ExamConfigurationContentProps {
  selectedExamTypeUuid: string;
  selectedExamTypeDisplay?: string;
  setNoAccess: Dispatch<SetStateAction<boolean>>;
}

const {FORBIDDEN} = StatusCodes;

const ExamConfigurationContent = ({
                                    selectedExamTypeUuid,
                                    selectedExamTypeDisplay,
                                    setNoAccess,
                                  }: ExamConfigurationContentProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const {classes} = useStyles();
  const [selectedYear, setSelectedYear] = useState<number>(0);

  const [admissionLevel, setAdmissionLevel] = useState<string>('');
  const [dateOfBirthFrom, setDateOfBirthFrom] = useState<string>('');
  const [dateOfBirthTo, setDateOfBirthTo] = useState<string>('');
  const [examVacancy, setExamVacancy] = useState<string>('');
  const [deletedAdmissionLevel, setDeletedAdmissionLevel] = useState<string>('');
  const [deletedAdmissionLevelUuid, setDeletedAdmissionLevelUuid] = useState<string>('');

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [openAddAdmissionModal, setOpenAddAdmissionModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleForbiddenError = (err: AxiosError) => {
    if (err.response?.status === FORBIDDEN) {
      setNoAccess(true);
    }
  };

  const {data: admissionYears} = useGetAdmissionYears({
    onSuccess: (res) => {
      setSelectedYear(res[0]);
    },
    onError: handleForbiddenError,
  });

  const {
    data: admissionLevelVacancies,
    isFetching: isFetchingAdmissionLevelVacancies,
    refetch: refetchAdmissionLevelVacancies,
  } = useGetAdmissionLevelVacancies({
    year: selectedYear,
    uuid: selectedExamTypeUuid,
    onError: handleForbiddenError,
    enabled: selectedYear !== 0,
  });

  const {state: tableState, dispatch: tableDispatch, tOptions} = useDataTable({
    ...TABLE_INITIAL_STATE,
  });

  const {data: unUsedExamLevels, refetch: refetchUnusedExamLevels} = useGetUnusedExamLevels({
    year: selectedYear,
    uuid: selectedExamTypeUuid,
    enabled: selectedYear !== 0,
  });

  const {data: lastUpdatedInfo, refetch: refetchLastUpdatedInfo} = useGetLastUpdatedInfo({
    year: selectedYear,
    uuid: selectedExamTypeUuid,
    enabled: selectedYear !== 0,
  });

  const deleteAdmissionLevelVacancies = useDeleteAdmissionLevelVacancies({
    onSuccess: () => {
      refetchAdmissionLevelVacancies();
      refetchLastUpdatedInfo();
    },
  });

  const handleCloseAddAdmissionModal = () => {
    setAdmissionLevel('');
    setDateOfBirthFrom('');
    setDateOfBirthTo('');
    setExamVacancy('');
    setApiButtonStatus('idle');
    setOpenAddAdmissionModal(false);
  };

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deletedAdmissionLevelUuid) {
      deleteAdmissionLevelVacancies.mutate(deletedAdmissionLevelUuid);
    }
    handleCloseDeleteModal();
  };

  const authorities = useGetUserAuthorities();

  const hasCreateAccess = existInArray(
      authorities.data,
      AUTHORITY_NAMES.CP_ADMIN_YEAR_OF_ADMISSION_AND_EXAM_VACANCY_CREATE
  );
  const hasDeleteAccess = existInArray(
      authorities.data,
      AUTHORITY_NAMES.CP_ADMIN_YEAR_OF_ADMISSION_AND_EXAM_VACANCY_DELETE
  );

  return (
      <>
        <ContentTemplate type="full">
          {selectedExamTypeDisplay && (
              <SectionHeader
                  title={t('exam->>ExamConfiguration.ExamConfigurationTitleHeader', {
                    examType: selectedExamTypeDisplay,
                  })}
              />
          )}
          <Typography>{t('exam->>ExamConfiguration.Statement')} </Typography>
          {admissionYears && (
              <Box className={classes.headerContainer}>
                <CustomSelect
                    id="inputSelect-yearOfAdmission"
                    isHeader
                    displayEmpty
                    onChange={(e) => setSelectedYear(e.target.value as number)}
                    menuItems={[
                      {
                        children: admissionYears[0],
                        value: admissionYears[0],
                        key: admissionYears[0],
                      },
                      {
                        children: admissionYears[1],
                        value: admissionYears[1],
                        key: admissionYears[1],
                      },
                    ]}
                    variant="filled"
                    defaultValue={admissionYears[0]}
                    placeholder={t('exam->>ExamConfiguration.SelectYearOfAdmission')}
                    label={t('exam->>ExamConfiguration.YearOfAdmission')}
                    rootClass={classes.root}
                />
                {hasCreateAccess && (
                    <Button
                        id="add-admission-level-btn"
                        variant="text"
                        color="primary"
                        startIcon={
                          <Icon name={IconName.add} size="S" color={theme.color.icon.iconSelected}/>
                        }
                        onClick={() => {
                          setOpenAddAdmissionModal(true);
                          setApiButtonStatus('idle');
                          setDateOfBirthFrom(defaultDateOfBirthFrom);
                          setDateOfBirthTo(defaultDateOfBirthTo);
                          refetchUnusedExamLevels();
                        }}
                        className={classes.addAdmissionLevelButton}
                    >
                      <Typography variant="h4">
                        {t('exam->>ExamConfiguration.AddAdmissionLevel')}
                      </Typography>
                    </Button>
                )}
              </Box>
          )}
          <MUIDataTable
              isLoading={isFetchingAdmissionLevelVacancies}
              columns={useGetTableColumns(
                  admissionLevelVacancies,
                  hasDeleteAccess,
                  setOpenDeleteModal,
                  setDeletedAdmissionLevel,
                  setDeletedAdmissionLevelUuid
              )}
              data={admissionLevelVacancies}
              dispatch={tableDispatch}
              state={tableState}
              emptyDataLabel={t('exam->>ExamConfiguration.EmptyDataLabel')}
              options={{
                ...tOptions,
              }}
          />
          {lastUpdatedInfo && <DraftDateTimeStamp lastUpdatedInfo={lastUpdatedInfo}/>}
        </ContentTemplate>
        <AddAdmissionLevelModal
            modalState={openAddAdmissionModal}
            onDialogClose={handleCloseAddAdmissionModal}
            selectedYear={selectedYear}
            selectedExamTypeUuid={selectedExamTypeUuid}
            admissionLevel={admissionLevel}
            dateOfBirthFrom={dateOfBirthFrom}
            dateOfBirthTo={dateOfBirthTo}
            examVacancy={examVacancy}
            apiButtonStatus={apiButtonStatus}
            setAdmissionLevel={setAdmissionLevel}
            setDateOfBirthFrom={setDateOfBirthFrom}
            setDateOfBirthTo={setDateOfBirthTo}
            setExamVacancy={setExamVacancy}
            setApiButtonStatus={setApiButtonStatus}
            refetchAdmissionLevelVacancies={refetchAdmissionLevelVacancies}
            unUsedExamLevels={unUsedExamLevels}
            refetchLastUpdatedInfo={refetchLastUpdatedInfo}
        />

        <CustomDialog
            open={openDeleteModal}
            title={t('exam->>ExamConfiguration.RemoveAdmissionLevelTitle')}
            message={
              <HtmlTrans
                  i18nKey="exam->>ExamConfiguration.RemoveAdmissionLevelStatement"
                  values={{
                    boldContent: deletedAdmissionLevel,
                  }}
              />
            }
            action={
              <APIActions
                  onClose={handleCloseDeleteModal}
                  apiCall={handleDelete}
                  cancelString={t('exam->>ExamConfiguration.Cancel')}
                  apiString={t('exam->>ExamConfiguration.Proceed')}
              />
            }
        />
      </>
  );
};

export default ExamConfigurationContent;
